// Global Instructions Rule Applied!

import { FundraisingState, FundraisingAction, FundraisingTarget } from '../../interfaces/fundraising.ts'
import {
  SET_FILTER_OPTIONS,
  SET_FUNDRAISING,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  CLEAR_ACTIVE_SORTING,
  UPDATE_PROSPECT_RECORD,
  ADD_PROSPECT,
  APPEND_FUNDRAISING,
  SET_PIPELINE_SELECTED_FUND,
  REMOVE_PROSPECT_FROM_LIST
} from './constants.ts'
import { UPDATE_RECORD_BY_SEARCH } from '../../sync_engine/constants.ts'

const getProspectId = (prospect: { id?: string; prospects__id?: string }) =>
  (prospect.id || prospect.prospects__id)?.toString()

/** Match PATCH/onPatchSuccess ids whether the row uses `id` or `prospects__id` as the canonical key */
const prospectRowMatchesId = (
  prospect: { id?: string; prospects__id?: string; [key: string]: any },
  prospectIdStr: string
): boolean => {
  const candidates = [prospect.id, prospect.prospects__id, prospect['prospects__id']]
    .filter((v) => v != null && v !== '')
    .map((v) => String(v))
  return candidates.some((c) => c === prospectIdStr)
}

/** Fallback board fund when `pipeline.selectedFundId` is unset.
 *
 * TODO (fund status, 2026-09-04): this is the LAST hardcoded PROD fund uuid in
 * the frontend — a CLAUDE.md violation, and meaningless on UAT. The canonical
 * value now lives in cfg (app_config fundraising.default_pipeline_fund_id,
 * seeded by 20261106120600 and resolved there by NAME); PipelineFundSelect and
 * the kanban both read it from globalConfigs.
 *
 * It survives here only because this is a REDUCER and cannot read the store.
 * Removing it means deciding what "no fund selected" should match — everything,
 * or nothing — which changes what appears on the board, so it is deliberately
 * left for the owner rather than guessed at. The path when that is decided is
 * to thread the id through the action payload. */
const DEFAULT_PIPELINE_BOARD_FUND_ID = '811ec194-3317-ee11-8f6e-000d3a1fdf30'

const prospectFundMatchesPipelineBoard = (
  record: { [key: string]: any } | null | undefined,
  selectedFundId: string | null | undefined
): boolean => {
  if (!record) return false
  const fundId = record['prospects__fund'] ?? record.prospects__fund
  if (fundId == null || fundId === '') return false
  const effective = selectedFundId ?? DEFAULT_PIPELINE_BOARD_FUND_ID
  return String(fundId) === String(effective)
}

const fundraisingReducer = (
  state: FundraisingState = {
    filterOptions: {
      data: {}
    },
    activeFilters: [],
    activeSorting: [],
    data: [],
    hasMore: false,
    currentPage: 1,
    pipeline: { data: [] }
  },
  action: FundraisingAction
): FundraisingState => {
  switch (action.type) {
    case SET_FILTER_OPTIONS:
      const { response: filterOptionsResponse } = action.payload

      return {
        ...state,
        filterOptions: filterOptionsResponse
      }

    case SET_FUNDRAISING: {
      const { response: fundraisingResponse, target } = action.payload
      const effectiveTarget = target ?? FundraisingTarget.FUNDRAISING

      if (effectiveTarget === FundraisingTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            data: fundraisingResponse.data
          }
        }
      }

      return {
        ...state,
        data: fundraisingResponse.data,
        hasMore: fundraisingResponse.hasMore,
        currentPage: 1
      }
    }

    case SET_ACTIVE_FILTERS:
      const { filters: activeFilters } = action.payload

      return {
        ...state,
        activeFilters
      }

    case SET_ACTIVE_SORTING:
      const { sorting: activeSorting } = action.payload

      return {
        ...state,
        activeSorting
      }

    case CLEAR_ACTIVE_FILTERS:
      return {
        ...state,
        activeFilters: []
      }

    case CLEAR_ACTIVE_SORTING:
      return {
        ...state,
        activeSorting: []
      }

    case UPDATE_PROSPECT_RECORD: {
      const { prospectId, updates } = action.payload
      const prospectIdStr = prospectId.toString()

      const prospectExistsInData = state.data.some((p) => prospectRowMatchesId(p, prospectIdStr))
      const prospectExistsInPipeline =
        state.pipeline?.data?.some((p) => prospectRowMatchesId(p, prospectIdStr)) ?? false

      const updatesIdField = updates['prospects__id'] || updates.prospects__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === prospectId || updatesIdField === String(prospectId)) && updateFieldCount > 5

      // Allow full API payloads to apply when the row was dropped from pipeline.data
      // (e.g. after a fund-filtered refetch) so the board can show the card again without a manual refresh.
      if (!prospectExistsInData && !prospectExistsInPipeline && !isFullRecordRefresh) {
        return state
      }

      const mapProspect = (prospect: { id?: string; prospects__id?: string; [key: string]: any }) => {
        if (!prospectRowMatchesId(prospect, prospectIdStr)) return prospect
        if (isFullRecordRefresh) {
          const { tasks, ...rest } = updates
          return rest
        }
        const updated = { ...prospect, ...updates }
        if (updates['prog__tasks'] !== undefined && 'tasks' in updated) {
          const { tasks, ...rest } = updated
          return rest
        }
        return updated
      }

      let newData = prospectExistsInData ? state.data.map(mapProspect) : state.data
      let newPipelineData = prospectExistsInPipeline ? state.pipeline.data.map(mapProspect) : state.pipeline.data

      const selectedFundId = state.pipeline?.selectedFundId

      let mergedForId: { [key: string]: any } | null = null
      if (prospectExistsInData) {
        mergedForId = newData.find((p) => prospectRowMatchesId(p, prospectIdStr)) ?? null
      } else if (prospectExistsInPipeline) {
        mergedForId = newPipelineData.find((p) => prospectRowMatchesId(p, prospectIdStr)) ?? null
      } else if (isFullRecordRefresh) {
        const { tasks, ...rest } = updates
        mergedForId = rest
      }

      const alreadyInPipeline = newPipelineData.some((p) => prospectRowMatchesId(p, prospectIdStr))
      if (
        !alreadyInPipeline &&
        mergedForId &&
        prospectFundMatchesPipelineBoard(mergedForId, selectedFundId)
      ) {
        newPipelineData = [mergedForId, ...newPipelineData]
      }

      return {
        ...state,
        data: newData,
        pipeline: {
          ...state.pipeline,
          data: newPipelineData
        }
      }
    }

    case ADD_PROSPECT:
      const { prospect: newProspect } = action.payload

      return {
        ...state,
        data: [newProspect, ...state.data],
        pipeline: {
          ...state.pipeline,
          data: [newProspect, ...(state.pipeline?.data ?? [])]
        }
      }

    case APPEND_FUNDRAISING:
      const { response: appendResponse } = action.payload

      return {
        ...state,
        data: [...state.data, ...appendResponse.data],
        hasMore: appendResponse.hasMore,
        currentPage: state.currentPage + 1
      }

    case SET_PIPELINE_SELECTED_FUND: {
      const { selectedFundId } = action.payload
      return {
        ...state,
        pipeline: {
          ...state.pipeline,
          selectedFundId
        }
      }
    }

    case REMOVE_PROSPECT_FROM_LIST: {
      const { prospectId } = action.payload
      const prospectIdStr = prospectId.toString()
      return {
        ...state,
        data: state.data.filter((p) => !prospectRowMatchesId(p, prospectIdStr)),
        pipeline: {
          ...state.pipeline,
          data: (state.pipeline?.data ?? []).filter((p) => !prospectRowMatchesId(p, prospectIdStr))
        }
      }
    }

    case UPDATE_RECORD_BY_SEARCH: {
      const { searchField, searchValue, updates } = action.payload

      return {
        ...state,
        data: state.data.map((record) => {
          const recordValue = record[searchField]
          const matches =
            recordValue === searchValue || String(recordValue) === String(searchValue) || recordValue == searchValue

          if (matches) {
            const filteredUpdates = Object.keys(updates).reduce(
              (acc, key) => {
                if (key in record) {
                  acc[key] = updates[key]
                }
                return acc
              },
              {} as Record<string, any>
            )

            if (Object.keys(filteredUpdates).length > 0) {
              const updated = { ...record, ...filteredUpdates }
              // If prog__tasks is being updated, remove old 'tasks' field to prevent duplicates
              if (filteredUpdates['prog__tasks'] !== undefined && 'tasks' in updated) {
                const { tasks, ...rest } = updated
                return rest
              }
              return updated
            }
          }
          return record
        })
      }
    }

    default:
      return state
  }
}

export default fundraisingReducer
export * from './fundraising_actions.ts'
export * from './fundraising_services.ts'
export * from './selectors.ts'
